import { useCallback, useRef } from "react";
import { PopoutSize } from "./usePopoutFit";

/**
 * Measures the modal box a dialog is docked in, so a pop-out can open at the size the
 * user was already looking at.
 *
 * Put `dockedRef` on the dialog's first child, docked branch only: `Modal` renders its
 * children straight into the box, so the box is that child's parent.
 */
export function useDockedSize<TElement extends HTMLElement>() {
  const dockedRef = useRef<TElement | null>(null);

  return {
    dockedRef,
    /** Call at pop-out time, not render time — the box is sized by layout, not props. */
    dockedSize: useCallback((): PopoutSize | undefined => {
      const box = dockedRef.current?.parentElement;
      if (!box) return undefined;

      const { width, height } = box.getBoundingClientRect();
      if (width <= 0 || height <= 0) return undefined;
      return { width: Math.round(width), height: Math.round(height) };
    }, []),
  };
}
